import type { TableCellNode, TableNode } from '@lexical/table';
import { $findCellNode, $findTableNode, $getTableCellNodeRect, $isTableCellNode, $isTableSelection, TableCellHeaderStates } from '@lexical/table';
import type { NodeKey } from 'lexical';
import { $getNearestNodeFromDOMNode, $getSelection, $isRangeSelection } from 'lexical';

/** The cell holding the caret (or anchoring a cell selection), plus every cell an action targets. */
export const $getSelectedCells = (): { cell: TableCellNode; cells: TableCellNode[] } | null => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) && !$isTableSelection(selection)) return null;

  const cell = $findCellNode(selection.anchor.getNode());
  if (!cell) return null;

  const cells = $isTableSelection(selection) ? selection.getNodes().filter($isTableCellNode) : [cell];
  return { cell, cells };
};

/** The table around the current selection, for table-wide actions. */
export const $getSelectedTable = (): TableNode | null => {
  const selected = $getSelectedCells();
  return selected && $findTableNode(selected.cell);
};

/** What the cell menu shows for the current selection: toggle states and which actions apply. */
export type ActiveCell = {
  cellKey: NodeKey;
  rowIndex: number;
  columnIndex: number;
  rowIsHeader: boolean;
  columnIsHeader: boolean;
  striped: boolean;
  canMerge: boolean;
  canUnmerge: boolean;
  verticalAlign: string;
};

export const $readActiveCell = (): ActiveCell | null => {
  const selected = $getSelectedCells();
  const table = selected && $findTableNode(selected.cell);
  const rect = selected && $getTableCellNodeRect(selected.cell);
  if (!selected || !table || !rect) return null;

  const { cell, cells } = selected;
  return {
    cellKey: cell.getKey(),
    rowIndex: rect.rowIndex,
    columnIndex: rect.columnIndex,
    rowIsHeader: cell.hasHeaderState(TableCellHeaderStates.ROW),
    columnIsHeader: cell.hasHeaderState(TableCellHeaderStates.COLUMN),
    striped: table.getRowStriping(),
    canMerge: cells.length > 1,
    canUnmerge: cells.length === 1 && (cell.getColSpan() > 1 || cell.getRowSpan() > 1),
    verticalAlign: cell.getVerticalAlign() ?? 'top',
  };
};

/** The cell, its table and its grid position for a DOM node inside a cell (for pointer-driven UI). */
export const $getCellFromDOM = (domNode: Node) => {
  const node = $getNearestNodeFromDOMNode(domNode);
  const cell = node && $findCellNode(node);
  const table = cell && $findTableNode(cell);
  const rect = cell && $getTableCellNodeRect(cell);
  return cell && table && rect ? { cell, table, rect } : null;
};
